import { useEffect, useMemo, useState } from 'react';

export type SeoPathMatcher = string | RegExp;

export interface SeoMetaManagerProps {
  canonicalOrigin?: string;
  productionHostnames?: string[];
  publicPaths?: SeoPathMatcher[];
  defaultRobots?: string;
  publicRobots?: string;
}

const DEFAULT_CANONICAL_ORIGIN = 'https://www.hypedirector.com';
const DEFAULT_PUBLIC_ROBOTS = 'index,follow';
const DEFAULT_PRIVATE_ROBOTS = 'noindex,nofollow';

const normalizePath = (path: string): string => {
  if (!path || path === '/') {
    return '/';
  }
  return `/${path.replace(/^\/+|\/+$/g, '')}`;
};

const getCurrentPath = (): string => normalizePath(window.location.pathname);

const pathMatches = (path: string, matcher: SeoPathMatcher): boolean => {
  if (typeof matcher === 'string') {
    return normalizePath(matcher) === path;
  }
  return matcher.test(path);
};

const isProductionHost = (hostnames: string[]): boolean => {
  const hostname = window.location.hostname.toLowerCase();
  return hostnames.map((value) => value.toLowerCase()).includes(hostname);
};

const upsertMetaTag = (name: string, content: string): void => {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const upsertCanonicalLink = (href: string): void => {
  let tag = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

const removeCanonicalLink = (): void => {
  document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.remove();
};

const subscribeToLocationChanges = (callback: () => void): (() => void) => {
  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function pushState(...args) {
    const result = originalPushState.apply(this, args);
    callback();
    return result;
  };

  window.history.replaceState = function replaceState(...args) {
    const result = originalReplaceState.apply(this, args);
    callback();
    return result;
  };

  window.addEventListener('popstate', callback);

  return () => {
    window.history.pushState = originalPushState;
    window.history.replaceState = originalReplaceState;
    window.removeEventListener('popstate', callback);
  };
};

export const SeoMetaManager: React.FC<SeoMetaManagerProps> = ({
  canonicalOrigin = DEFAULT_CANONICAL_ORIGIN,
  productionHostnames = ['www.hypedirector.com', 'hypedirector.com'],
  publicPaths = [],
  defaultRobots = DEFAULT_PRIVATE_ROBOTS,
  publicRobots = DEFAULT_PUBLIC_ROBOTS,
}) => {
  const [path, setPath] = useState(() => {
    if (typeof window === 'undefined') {
      return '/';
    }
    return getCurrentPath();
  });

  const normalizedPublicPaths = useMemo(() => publicPaths, [publicPaths]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const updatePath = () => setPath(getCurrentPath());
    updatePath();
    return subscribeToLocationChanges(updatePath);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const publicPage = normalizedPublicPaths.some((matcher) => pathMatches(path, matcher));
    const production = isProductionHost(productionHostnames);

    if (production && publicPage) {
      const origin = canonicalOrigin.replace(/\/+$/g, '');
      upsertMetaTag('robots', publicRobots);
      upsertCanonicalLink(`${origin}${path === '/' ? '/' : path}`);
      return;
    }

    upsertMetaTag('robots', defaultRobots);
    removeCanonicalLink();
  }, [canonicalOrigin, defaultRobots, normalizedPublicPaths, path, productionHostnames, publicRobots]);

  return null;
};
